import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, Bike, Star, LogOut, MapPin, Phone, MessageCircle, Utensils, Navigation, Clock,
  X, Package, User, Home as HomeIcon, Info, ChevronRight, ClipboardCheck, Copy, Camera, Shield, Check,
  TrendingUp, BarChart3, Calendar, CheckCircle2, ArrowRight,
} from "lucide-react";
import MapMock from "@/components/customer/MapMock";
import DriverDeliveryMap from "@/components/maps/DriverDeliveryMap";
import ProofCameraModal from "@/components/driver/ProofCameraModal";
import OrderChatDrawer from "@/components/OrderChatDrawer";
import ContactDetailsModal from "@/components/ContactDetailsModal";
import { playDriverDing, playSuccessChime, startCaribbeanAlert } from "@/lib/sounds";

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [online, setOnline] = useState(user?.online || false);
  const [earnings, setEarnings] = useState({ today: 0, week: 0, trips_today: 0, completed: 0, acceptance_rate: 96, rating: 4.9 });
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [detailOrder, setDetailOrder] = useState(null);   // full details sheet
  const [offerModal, setOfferModal] = useState(null);     // Uber-style incoming request modal
  const [proofOrder, setProofOrder] = useState(null);     // order awaiting delivery photo
  const [pickupPhotoOrder, setPickupPhotoOrder] = useState(null); // order awaiting pickup photo
  const [tripComplete, setTripComplete] = useState(null); // { earn: number } after delivery closes
  const [showEarnings, setShowEarnings] = useState(null); // null | "today" | "week"
  const [chatOrder, setChatOrder] = useState(null); // active-trip order the driver is chatting with the customer about
  const prevOfferIdsRef = useRef(new Set());
  const shownOfferIdsRef = useRef(new Set());
  const dismissedOfferIdsRef = useRef(new Set());
  const autoPickupPromptedRef = useRef(new Set()); // orders we've auto-opened the pickup camera for
  const refreshRef = useRef(null); // always points to the latest `refresh` closure — used by SSE handlers
  const soundEnabledRef = useRef(false);

  // Enable sound only after user interacts (browser autoplay policy)
  useEffect(() => {
    const enable = () => { soundEnabledRef.current = true; window.removeEventListener("pointerdown", enable); };
    window.addEventListener("pointerdown", enable, { once: true });
    return () => window.removeEventListener("pointerdown", enable);
  }, []);

  const refresh = async () => {
    try {
      const [e, av, my] = await Promise.all([
        api.get("/driver/earnings").then((r) => r.data),
        api.get("/driver/available-orders").then((r) => r.data),
        api.get("/orders/mine").then((r) => r.data),
      ]);
      // Detect new available offers -> ding + auto-popup competitive modal
      const newIds = av.map((o) => o.order_id);
      const prev = prevOfferIdsRef.current;
      const fresh = newIds.filter((id) => !prev.has(id));
      if (fresh.length && prev.size > 0 && soundEnabledRef.current) {
        playDriverDing();
        toast.success(`New offer${fresh.length > 1 ? "s" : ""} nearby!`);
      }
      prevOfferIdsRef.current = new Set(newIds);

      // Auto-popup the first genuinely-new offer as a full-screen Uber-style request,
      // but only if the driver is online, has no active trip, no modal already open,
      // and hasn't already seen (or dismissed) this offer.
      const activeMine = my.find((o) => !["delivered", "cancelled"].includes(o.status));
      if (
        online && !activeMine && !offerModal && !proofOrder && !pickupPhotoOrder && soundEnabledRef.current
      ) {
        const nextOffer = av.find(
          (o) =>
            !shownOfferIdsRef.current.has(o.order_id) &&
            !dismissedOfferIdsRef.current.has(o.order_id)
        );
        if (nextOffer) {
          shownOfferIdsRef.current.add(nextOffer.order_id);
          setOfferModal(nextOffer);
        }
      }

      setEarnings(e); setAvailable(av); setMine(my);
      // Keep the open detail sheet in sync with fresh data
      if (detailOrder) {
        const fresh = [...my, ...av].find((o) => o.order_id === detailOrder.order_id);
        if (fresh) setDetailOrder(fresh);
      }

      // Auto-open pickup camera the moment the restaurant marks the order picked_up.
      // Fires once per order; the driver can Snooze/Retry via the manual "On the way" button.
      if (!pickupPhotoOrder && !proofOrder && !offerModal) {
        const needsPickupPhoto = my.find(
          (o) =>
            o.status === "picked_up" &&
            !o.pickup_photo_image &&
            !autoPickupPromptedRef.current.has(o.order_id),
        );
        if (needsPickupPhoto) {
          autoPickupPromptedRef.current.add(needsPickupPhoto.order_id);
          setPickupPhotoOrder(needsPickupPhoto);
          if (soundEnabledRef.current) playDriverDing();
          toast.success("Order picked up — snap a quick photo to continue");
        }
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    // Keep the ref in sync so the SSE handlers (registered once at mount)
    // always call the latest `refresh` closure — otherwise they'd hold a
    // stale reference to the first render's state and toasts.
    refreshRef.current = refresh;
  });

  useEffect(() => {
    // Initial fetch plus a low-frequency safety-net poll (every 30s) so the
    // dashboard self-heals if the SSE stream is dropped by a proxy.
    refreshRef.current?.();
    const poll = setInterval(() => refreshRef.current?.(), 30000);

    // Real-time driver notifications via Server-Sent Events. The stream fires
    // the instant a restaurant accepts an order, so the offer modal appears
    // within milliseconds instead of up to 6s later.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backend}/api/driver/events`;
    let source;
    try {
      source = new EventSource(url, { withCredentials: true });
      source.addEventListener("offer_available", () => { refreshRef.current?.(); });
      source.addEventListener("offer_gone", () => { refreshRef.current?.(); });
      // Smart Proximity targeted offer — only auto-open the modal when THIS
      // driver is the current target of the dispatcher.
      source.addEventListener("targeted_offer", (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.target_driver_id && payload.target_driver_id !== user?.user_id) return;
          refreshRef.current?.(); // pulls the fresh offer into available-orders so the modal opens
        } catch { /* noop */ }
      });
      source.addEventListener("offer_expired", (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.target_driver_id && payload.target_driver_id !== user?.user_id) return;
          setOfferModal((cur) => (cur && cur.order_id === payload.order_id ? null : cur));
          refreshRef.current?.();
        } catch { /* noop */ }
      });
      // Restaurant flipped the food to "ready" — nudge the assigned driver to
      // head to the counter. Filtered so unrelated drivers ignore it.
      source.addEventListener("order_ready", (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.target_driver_id && payload.target_driver_id !== user?.user_id) return;
          if (soundEnabledRef.current) playDriverDing();
          const pin = payload.pickup_code ? ` · PIN ${String(payload.pickup_code).padStart(4, "0")}` : "";
          toast.success(`Food is ready — head to the counter${pin}`, { duration: 6000 });
          refreshRef.current?.();
        } catch { /* noop */ }
      });
      // Restaurant reported a kitchen delay — surface it to the assigned driver.
      source.addEventListener("order_delayed", (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.target_driver_id && payload.target_driver_id !== user?.user_id) return;
          if (soundEnabledRef.current) playDriverDing();
          toast.warning(
            `Kitchen delay: +${payload.delay_minutes} min · new ETA ${payload.new_eta_minutes} min`,
            { duration: 6000 },
          );
          refreshRef.current?.();
        } catch { /* noop */ }
      });
      source.onerror = () => { /* browser auto-reconnects; polling is the safety net */ };
    } catch { /* EventSource unsupported → polling fallback still works */ }

    return () => {
      clearInterval(poll);
      try { source && source.close(); } catch { /* noop */ }
    };
    /* eslint-disable-next-line */
  }, []);

  const toggle = async () => {
    try {
      const { data } = await api.post("/driver/toggle-online", { online: !online });
      setOnline(data.online);
      toast.success(data.online ? "You're online — orders will roll in!" : "Going offline");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const accept = async (order) => {
    try {
      await api.post(`/orders/${order.order_id}/accept`);
      toast.success("Order accepted");
      setOfferModal(null);
      const { data: fresh } = await api.get(`/orders/${order.order_id}`);
      setDetailOrder(fresh);
      refresh();
    } catch (e) {
      // 409 = another driver already claimed this order
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Sorry, another driver got the order");
        setOfferModal(null);
      } else {
        toast.error(formatApiError(e));
      }
      refresh();
    }
  };

  const setStatus = async (orderId, status) => {
    const target = mine.find((o) => o.order_id === orderId);
    // Delivered requires proof capture — open camera modal instead of a direct update
    if (status === "delivered" && target) {
      setProofOrder(target);
      return;
    }
    // On-the-way requires a pickup photo first (once) — Uber-style "food leaving restaurant" evidence
    if (status === "on_the_way" && target && !target.pickup_photo_image) {
      setPickupPhotoOrder(target);
      return;
    }
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitPickupPhoto = async (imageBase64) => {
    if (!pickupPhotoOrder) return;
    try {
      await api.post(`/orders/${pickupPhotoOrder.order_id}/pickup-photo`, { image_base64: imageBase64 });
      playSuccessChime();
      // Close every overlay so the driver's next screen is the delivery map.
      setPickupPhotoOrder(null);
      setDetailOrder(null);
      setOfferModal(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("On the way to the customer — drive safe!");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitProof = async (imageBase64) => {
    if (!proofOrder) return;
    try {
      await api.post(`/orders/${proofOrder.order_id}/proof`, { image_base64: imageBase64 });
      playSuccessChime();
      const earn = driverEarnOf(proofOrder);
      // Close every overlay and route the driver back to the home dashboard,
      // then celebrate with a brief "trip complete" screen that auto-dismisses.
      setProofOrder(null);
      setDetailOrder(null);
      setOfferModal(null);
      setPickupPhotoOrder(null);
      setTripComplete({ earn });
      window.scrollTo({ top: 0, behavior: "smooth" });
      refresh();
      setTimeout(() => setTripComplete(null), 2600);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeTrip = mine.find((o) => !["delivered", "cancelled"].includes(o.status));

  // Broadcast the driver's live GPS to the backend while heading to the customer.
  // The customer's tracking page polls the order and sees the marker move.
  useEffect(() => {
    if (!activeTrip || activeTrip.status !== "on_the_way") return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let lastPush = 0;
    const orderId = activeTrip.order_id;
    const push = (lat, lng) => {
      const now = Date.now();
      if (now - lastPush < 5000) return; // throttle to 1 push per 5s
      lastPush = now;
      api.post(`/orders/${orderId}/driver-location`, { lat, lng }).catch(() => {});
    };
    // Kick one off immediately with a single-shot getCurrentPosition,
    // then subscribe to watchPosition for continuous updates.
    navigator.geolocation.getCurrentPosition(
      (pos) => push(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
    );
    const watchId = navigator.geolocation.watchPosition(
      (pos) => push(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
    return () => { try { navigator.geolocation.clearWatch(watchId); } catch { /* noop */ } };
  }, [activeTrip?.order_id, activeTrip?.status]);

  // Idle-online GPS broadcast — while the driver is online but not on a trip,
  // push a heartbeat location every 15s so the Smart Proximity dispatcher can
  // rank them for incoming orders.
  useEffect(() => {
    if (!online || activeTrip) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    let cancelled = false;
    const push = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          api.post("/driver/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
      );
    };
    push();
    const t = setInterval(push, 15_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [online, activeTrip?.order_id]);

  return (
    <div className="min-h-screen bg-eatup-dark-bg text-white flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass-dark">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/eats-gnd-logo.png" alt="Eats GND" className="w-10 h-10 rounded-xl object-cover shadow-md" draggable={false} />
            <span className="font-display text-xl font-bold">Eats GND <span className="text-eatup-yellow">Driver</span></span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
            <span className={`w-2 h-2 rounded-full ${online ? "bg-eatup-green animate-pulse" : "bg-neutral-500"}`} />
            <span className="text-xs font-semibold">{online ? "Online" : "Offline"}</span>
          </div>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition" data-testid="driver-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Approval status banner — blocks operational access until an admin approves */}
      {user?.approval_status && user.approval_status !== "approved" && (
        <div
          data-testid="approval-status-banner"
          className={`px-5 py-3 border-b border-white/10 ${
            user.approval_status === "rejected"
              ? "bg-red-500/15"
              : "bg-eatup-yellow/10"
          }`}
        >
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <Clock className="w-4 h-4 text-eatup-yellow shrink-0" />
            <div className="text-xs sm:text-sm flex-1 min-w-0">
              {user.approval_status === "pending_docs" && (
                <>
                  <span className="font-black text-eatup-yellow uppercase tracking-widest">Finish your onboarding</span>
                  <span className="text-white/70"> — upload your documents to get reviewed.</span>
                </>
              )}
              {user.approval_status === "pending_review" && (
                <>
                  <span className="font-black text-eatup-yellow uppercase tracking-widest">Pending approval</span>
                  <span className="text-white/70"> — an Eats GND admin is reviewing your documents. You'll get access to trips once approved.</span>
                </>
              )}
              {user.approval_status === "rejected" && (
                <>
                  <span className="font-black text-red-300 uppercase tracking-widest">Application rejected</span>
                  <span className="text-white/70"> — {user.approval_notes || "please contact support to re-apply."}</span>
                </>
              )}
            </div>
            {(user.approval_status === "pending_docs" || user.approval_status === "rejected") && (
              <button
                onClick={() => navigate("/driver/onboarding")}
                data-testid="banner-continue-onboarding-btn"
                className={`text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full transition shrink-0 ${
                  user.approval_status === "rejected"
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "bg-eatup-yellow text-eatup-charcoal hover:bg-eatup-yellow/90"
                }`}
              >
                {user.approval_status === "rejected" ? "Fix documents" : "Continue"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Map */}
      <div className="relative flex-1 min-h-[380px]">
        {activeTrip ? (
          <DriverDeliveryMap
            pickupAddress={activeTrip.restaurant_name || activeTrip.restaurant_address}
            dropoffAddress={activeTrip.address}
            pickupCoords={activeTrip.pickup_lat != null ? { lat: activeTrip.pickup_lat, lng: activeTrip.pickup_lng } : null}
            dropoffCoords={activeTrip.dropoff_lat != null ? { lat: activeTrip.dropoff_lat, lng: activeTrip.dropoff_lng } : null}
            driverName={user?.name?.split(" ")[0] || "You"}
            dark
            height="100%"
          />
        ) : (
          <MapMock height="100%" dark driverName={user?.name?.split(" ")[0] || "You"} />
        )}

        <button
          onClick={() => setShowEarnings("today")}
          data-testid="driver-today-earnings-btn"
          className="absolute top-4 left-1/2 -translate-x-1/2 bg-white hover:bg-neutral-50 text-eatup-charcoal shadow-2xl rounded-full pl-5 pr-3 py-2.5 flex items-center gap-2 transition active:scale-[0.98]"
        >
          <DollarSign className="w-4 h-4 text-eatup-green" />
          <span className="font-display font-bold tabular text-xl" data-testid="driver-today-earnings">EC${earnings.today.toFixed(2)}</span>
          <span className="text-xs text-neutral-500">today</span>
          <span className="ml-1 w-6 h-6 rounded-full bg-eatup-charcoal text-white flex items-center justify-center">
            <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </button>

        {!activeTrip && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <button
              onClick={toggle}
              disabled={user?.approval_status && user.approval_status !== "approved"}
              data-testid="driver-go-btn"
              className={`relative w-32 h-32 rounded-full shadow-2xl flex items-center justify-center font-display font-extrabold text-3xl transition disabled:opacity-50 disabled:cursor-not-allowed ${
                online ? "bg-eatup-charcoal text-eatup-yellow" : "bg-eatup-green text-white"
              }`}
              style={{ boxShadow: online ? "0 0 60px rgba(24, 180, 91, 0.5)" : "0 0 60px rgba(24, 180, 91, 0.7)" }}
            >
              {online && <span className="absolute inset-0 rounded-full border-4 border-eatup-green/40 animate-ping" />}
              {user?.approval_status && user.approval_status !== "approved"
                ? "WAIT"
                : online ? "STOP" : "GO"}
            </button>
            <div className="mt-3 text-center text-xs text-white/70 font-semibold uppercase tracking-widest">
              {user?.approval_status === "pending_docs" && "Finish onboarding first"}
              {user?.approval_status === "pending_review" && "Awaiting admin approval"}
              {user?.approval_status === "rejected" && "Application not approved"}
              {(!user?.approval_status || user.approval_status === "approved") && (online ? "Tap to go offline" : "Tap to start earning")}
            </div>
            {online && (
              <div
                data-testid="driver-searching-indicator"
                className="mt-3 mx-auto flex items-center gap-2 rounded-full bg-eatup-green/15 border border-eatup-green/30 px-4 py-1.5 w-fit"
              >
                <span className="relative flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-eatup-green animate-ping" />
                  <span className="relative w-2 h-2 rounded-full bg-eatup-green" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-eatup-green">
                  Searching for orders…
                </span>
              </div>
            )}
          </div>
        )}

        {activeTrip && (
          <ActiveTripHero
            trip={activeTrip}
            onOpenDetails={() => setDetailOrder(activeTrip)}
            onChat={() => setChatOrder(activeTrip)}
          />
        )}
      </div>

      {/* Sticky primary action bar — thumb-reach CTA that matches the trip stage. */}
      {activeTrip && (
        <StickyTripAction
          trip={activeTrip}
          online={online}
          onTakePickup={() => setPickupPhotoOrder(activeTrip)}
          onTakeDelivery={() => setProofOrder(activeTrip)}
          onOpenDetails={() => setDetailOrder(activeTrip)}
        />
      )}

      {/* Bottom stats — big tappable cards, each drills into the earnings overlay for context. */}
      <section className={`bg-eatup-dark-surface border-t border-white/10 ${activeTrip ? "pb-24" : ""}`}>
        <div className="max-w-6xl mx-auto px-5 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              testid="stat-trips"
              label="Trips"
              value={earnings.trips_today}
              sub="Today"
              icon={Bike}
              onClick={() => setShowEarnings("today")}
            />
            <StatCard
              testid="stat-week"
              label="Week"
              value={`EC$${earnings.week.toFixed(0)}`}
              sub="Weekly total"
              icon={TrendingUp}
              accent
              onClick={() => setShowEarnings("week")}
            />
            <StatCard
              testid="stat-acceptance"
              label="Accept"
              value={`${earnings.acceptance_rate}%`}
              sub="Acceptance"
              icon={CheckCircle2}
              onClick={() => setShowEarnings("today")}
            />
            <StatCard
              testid="stat-rating"
              label="Rating"
              value={earnings.rating.toFixed(1)}
              sub="⭐ Customer"
              icon={Star}
              yellow
              onClick={() => setShowEarnings("today")}
            />
          </div>
        </div>
      </section>

      {/* Available orders */}
      {online && !activeTrip && available.length > 0 && (
        <section className="bg-eatup-dark-surface border-t border-white/10">
          <div className="max-w-6xl mx-auto px-5 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Nearby offers</h2>
              <div className="text-xs text-neutral-400">{available.length} available · tap any to view details</div>
            </div>
            <div className="space-y-3">
              {available.slice(0, 6).map((o) => (
                <OfferCard
                  key={o.order_id}
                  o={o}
                  onView={() => setDetailOrder(o)}
                  onQuickAccept={(e) => { e.stopPropagation(); accept(o); }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Accepted Trips — every order this driver has claimed but not yet delivered.
          Kept visible even during an active trip so the driver can see queued jobs
          and jump back into any of them. */}
      {(() => {
        const acceptedTrips = mine.filter((o) => !["delivered", "cancelled"].includes(o.status));
        if (acceptedTrips.length === 0) return null;
        return (
          <section className="bg-eatup-dark-surface border-t border-white/10" data-testid="accepted-trips-section">
            <div className="max-w-6xl mx-auto px-5 py-6">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <h2 className="font-display text-lg font-bold flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-eatup-green" /> Accepted trips
                  </h2>
                  <div className="text-xs text-neutral-400 mt-0.5">
                    {acceptedTrips.length} active · tap any to open the map & details
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-eatup-green font-black bg-eatup-green/15 border border-eatup-green/30 px-3 py-1 rounded-full">
                  {acceptedTrips.length}
                </div>
              </div>
              <div className="space-y-2.5">
                {acceptedTrips.map((o) => (
                  <AcceptedTripCard
                    key={o.order_id}
                    order={o}
                    isActive={activeTrip?.order_id === o.order_id}
                    onOpen={() => setDetailOrder(o)}
                  />
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Recent completed trips (also clickable) */}
      {mine.filter((o) => o.status === "delivered").length > 0 && (
        <section className="bg-eatup-dark-surface border-t border-white/10">
          <div className="max-w-6xl mx-auto px-5 py-6">
            <h2 className="font-display text-lg font-bold">Recent trips</h2>
            <div className="mt-3 space-y-2">
              {mine.filter((o) => o.status === "delivered").slice(0, 5).map((o) => (
                <button
                  key={o.order_id}
                  data-testid={`past-trip-${o.order_id}`}
                  onClick={() => setDetailOrder(o)}
                  className="w-full bg-eatup-dark-bg border border-white/10 rounded-2xl p-3 flex items-center gap-3 text-left hover:border-white/25 transition"
                >
                  <img src={o.restaurant_image} alt="" className="w-11 h-11 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{o.restaurant_name}</div>
                    <div className="text-xs text-neutral-400 truncate">Delivered · #{o.order_id.slice(-6).toUpperCase()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold tabular text-sm">EC${driverEarnOf(o).toFixed(2)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-neutral-500" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full details sheet */}
      {detailOrder && (
        <OrderDetailsSheet
          order={detailOrder}
          onClose={() => setDetailOrder(null)}
          onAccept={activeTrip ? null : () => accept(detailOrder)}
          onStatus={activeTrip && activeTrip.order_id === detailOrder.order_id ? (s) => setStatus(detailOrder.order_id, s) : null}
        />
      )}

      {/* Proof of delivery camera */}
      {proofOrder && (
        <ProofCameraModal
          onClose={() => setProofOrder(null)}
          onCapture={submitProof}
          title="Proof of delivery"
          subtitle="Snap the drop-off"
          cta="Submit & complete delivery"
        />
      )}

      {/* Pickup photo camera */}
      {pickupPhotoOrder && (
        <ProofCameraModal
          onClose={() => setPickupPhotoOrder(null)}
          onCapture={submitPickupPhoto}
          title="Food leaving the kitchen"
          subtitle="Snap the packed order"
          cta="Send pickup photo & start delivery"
        />
      )}

      {/* Full-screen incoming offer modal (Smart Proximity — closest driver first) */}
      {offerModal && (
        <OfferModal
          offer={offerModal}
          onAccept={() => accept(offerModal)}
          onDecline={() => {
            // Driver actively passed on the offer — tell the backend so the
            // Smart Proximity dispatcher immediately advances to the next
            // closest driver instead of waiting the full 30s.
            api.post(`/orders/${offerModal.order_id}/decline`).catch(() => {});
            dismissedOfferIdsRef.current.add(offerModal.order_id);
            setOfferModal(null);
            toast("Passed to the next driver.");
          }}
          onTimeout={() => {
            api.post(`/orders/${offerModal.order_id}/decline`).catch(() => {});
            dismissedOfferIdsRef.current.add(offerModal.order_id);
            setOfferModal(null);
            toast("Time's up — passing the order to another driver.");
          }}
        />
      )}

      {tripComplete && (
        <TripCompleteOverlay
          earn={tripComplete.earn}
          onClose={() => setTripComplete(null)}
        />
      )}

      {showEarnings && (
        <EarningsOverlay
          mine={mine}
          earnings={earnings}
          initialTab={showEarnings}
          onClose={() => setShowEarnings(null)}
        />
      )}

      {chatOrder && (
        <OrderChatDrawer
          orderId={chatOrder.order_id}
          meRole="driver"
          counterpartName={chatOrder.customer_name || "Customer"}
          onClose={() => setChatOrder(null)}
          dark
        />
      )}
    </div>
  );
}

function EarningsOverlay({ mine, earnings, onClose, initialTab = "today" }) {
  const [tab, setTab] = useState(initialTab); // "today" | "week"

  // All completed (delivered) trips.
  const delivered = useMemo(
    () => mine.filter((o) => o.status === "delivered"),
    [mine],
  );
  // Driver earnings preview — thin wrapper around the module-level helper.
  const earnOf = driverEarnOf;

  // ---- Today
  const todayStr = new Date().toDateString();
  const todayTrips = useMemo(
    () => delivered
      .filter((o) => new Date(o.created_at).toDateString() === todayStr)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [delivered, todayStr],
  );
  const todayTotal = todayTrips.reduce((s, o) => s + earnOf(o), 0);

  // ---- Last 7 days (including today)
  const weekBuckets = useMemo(() => {
    const buckets = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      buckets.push({
        key: d.toDateString(),
        date: d,
        label: d.toLocaleDateString([], { weekday: "short" }),
        day: d.getDate(),
        total: 0,
        trips: 0,
      });
    }
    delivered.forEach((o) => {
      const b = buckets.find((x) => new Date(o.created_at).toDateString() === x.key);
      if (b) { b.total += earnOf(o); b.trips += 1; }
    });
    return buckets;
  }, [delivered]);
  const weekTotal = weekBuckets.reduce((s, b) => s + b.total, 0);
  const weekPeak = Math.max(1, ...weekBuckets.map((b) => b.total));
  const bestDay = weekBuckets.reduce((best, b) => (b.total > best.total ? b : best), weekBuckets[0]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="earnings-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-2xl backdrop-saturate-150" onClick={onClose} />

      <div
        className="relative w-full max-w-lg rounded-[32px] border-2 border-eatup-green/40 shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10 flex flex-col max-h-[92vh] overflow-hidden"
        style={{
          background:
            "radial-gradient(120% 120% at 50% 0%, rgba(24,180,91,0.22) 0%, rgba(20,20,22,0.92) 65%)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
        }}
      >
        {/* Header */}
        <div className="p-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-eatup-green/20 text-eatup-green flex items-center justify-center">
            <DollarSign className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-eatup-yellow font-black">Your earnings</div>
            <div className="font-display font-black text-lg text-white">
              {tab === "today" ? "Today's trips" : "Last 7 days"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="earnings-close-btn"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 flex gap-2">
          <TabPill
            testid="tab-today"
            active={tab === "today"}
            onClick={() => setTab("today")}
            icon={Calendar}
            label="Today"
            hint={`EC$${todayTotal.toFixed(2)}`}
          />
          <TabPill
            testid="tab-week"
            active={tab === "week"}
            onClick={() => setTab("week")}
            icon={BarChart3}
            label="Weekly summary"
            hint={`EC$${weekTotal.toFixed(2)}`}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 mt-4 space-y-4">
          {tab === "today" ? (
            <TodayView todayTrips={todayTrips} todayTotal={todayTotal} earnOf={earnOf} />
          ) : (
            <WeekView buckets={weekBuckets} weekTotal={weekTotal} weekPeak={weekPeak} bestDay={bestDay} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabPill({ active, onClick, icon: Icon, label, hint, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-full text-sm font-semibold transition border ${
        active
          ? "bg-eatup-green/20 border-eatup-green/50 text-white"
          : "bg-white/5 border-white/10 text-neutral-300 hover:border-white/25"
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1 text-left truncate">{label}</span>
      <span className="tabular text-eatup-green font-bold text-xs">{hint}</span>
    </button>
  );
}

function TodayView({ todayTrips, todayTotal, earnOf }) {
  const todayBonus = todayTrips.reduce((s, o) => s + (o.tip_bonus || 0), 0);
  return (
    <>
      <div
        data-testid="today-total-card"
        className="rounded-3xl bg-eatup-green/15 border-2 border-eatup-green/40 p-5 flex items-baseline justify-between"
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest text-eatup-green font-black">Total earned today</div>
          <div className="mt-1 font-display font-black text-4xl tabular text-white drop-shadow-md">
            EC${todayTotal.toFixed(2)}
          </div>
          {todayBonus > 0 && (
            <div
              data-testid="today-bonus-tips"
              className="mt-1 text-[11px] font-bold uppercase tracking-widest text-eatup-yellow flex items-center gap-1"
            >
              <Star className="w-3 h-3 fill-eatup-yellow" />
              Incl. EC${todayBonus.toFixed(2)} bonus tips
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">Trips</div>
          <div className="font-display font-black text-2xl tabular text-white">{todayTrips.length}</div>
        </div>
      </div>

      {todayTrips.length === 0 ? (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-8 text-center text-neutral-300 text-sm" data-testid="today-empty">
          No trips delivered yet today. Go online and grab your first order!
        </div>
      ) : (
        <div className="rounded-3xl bg-white/5 border border-white/10 overflow-hidden">
          {todayTrips.map((o) => {
            const dt = new Date(o.created_at);
            const time = dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
            return (
              <div
                key={o.order_id}
                data-testid={`today-trip-${o.order_id}`}
                className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-b-0"
              >
                <div className="w-10 h-10 rounded-xl bg-eatup-charcoal text-eatup-yellow flex items-center justify-center shrink-0">
                  <Bike className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono font-bold text-eatup-green">
                    #{o.order_id.slice(-6).toUpperCase()}
                  </div>
                  <div className="text-sm font-semibold text-white truncate">
                    {o.restaurant_name}
                  </div>
                  <div className="text-[11px] text-neutral-400 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {time}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-eatup-green tabular text-lg">
                    +EC${earnOf(o).toFixed(2)}
                  </div>
                  <div className="text-[10px] text-neutral-500">
                    {o.driver_base_pay != null
                      ? `EC$${Number(o.driver_base_pay).toFixed(2)} base + EC$${(Number(o.driver_per_km_pay || 0) * Number(o.distance_km || 0)).toFixed(2)} dist + EC$${Number(o.tip || 0).toFixed(2)} tip`
                      : `EC$${(o.delivery_fee || 0).toFixed(2)} + EC$${(o.tip || 0).toFixed(2)} tip`}
                    {o.tip_bonus ? <span className="text-eatup-yellow font-bold"> + EC${o.tip_bonus.toFixed(2)} bonus</span> : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function WeekView({ buckets, weekTotal, weekPeak, bestDay }) {
  const avg = weekTotal / 7;
  return (
    <>
      <div className="rounded-3xl bg-eatup-green/15 border-2 border-eatup-green/40 p-5 grid grid-cols-3 gap-3" data-testid="week-summary-card">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-eatup-green font-black">Week total</div>
          <div className="mt-1 font-display font-black text-2xl tabular text-white">EC${weekTotal.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">Avg / day</div>
          <div className="mt-1 font-display font-black text-2xl tabular text-white">EC${avg.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">Best day</div>
          <div className="mt-1 font-display font-black text-2xl tabular text-white">{bestDay?.label || "—"}</div>
        </div>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 p-5" data-testid="week-chart">
        <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold mb-4">Daily earnings</div>
        <div className="flex items-end justify-between gap-2 h-40">
          {buckets.map((b) => {
            const pct = weekPeak > 0 ? (b.total / weekPeak) * 100 : 0;
            const isBest = b === bestDay && b.total > 0;
            return (
              <div key={b.key} className="flex-1 flex flex-col items-center gap-1.5">
                <div className={`text-[10px] font-bold tabular ${isBest ? "text-eatup-yellow" : "text-neutral-300"}`}>
                  ${b.total.toFixed(0)}
                </div>
                <div className="flex-1 w-full flex items-end">
                  <div
                    data-testid={`week-bar-${b.label.toLowerCase()}`}
                    className={`w-full rounded-t-lg transition-all duration-500 ${
                      isBest ? "bg-eatup-yellow" : "bg-eatup-green/60 hover:bg-eatup-green"
                    }`}
                    style={{ height: `${Math.max(4, pct)}%` }}
                    title={`${b.trips} trips`}
                  />
                </div>
                <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
                  {b.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 overflow-hidden" data-testid="week-list">
        {buckets.slice().reverse().map((b) => (
          <div
            key={b.key}
            className="flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-b-0"
            data-testid={`week-row-${b.label.toLowerCase()}`}
          >
            <div className="w-10 h-10 rounded-xl bg-eatup-charcoal text-eatup-yellow flex items-center justify-center shrink-0">
              <Calendar className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white">
                {b.label} <span className="text-neutral-500 font-normal">· {b.date.toLocaleDateString([], { month: "short", day: "numeric" })}</span>
              </div>
              <div className="text-[11px] text-neutral-400">
                {b.trips} trip{b.trips !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="font-display font-black text-eatup-green tabular text-base">
              ${b.total.toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function TripCompleteOverlay({ earn, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[65] flex items-center justify-center p-6 fade-up"
      data-testid="trip-complete-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-2xl backdrop-saturate-150" />
      <div
        className="relative w-full max-w-sm rounded-[32px] border-2 border-eatup-green/60 p-8 text-center shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
        style={{
          background:
            "radial-gradient(120% 120% at 50% 0%, rgba(24,180,91,0.28) 0%, rgba(20,20,22,0.90) 70%)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
        }}
      >
        <div className="relative mx-auto w-24 h-24 rounded-full bg-eatup-green flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-eatup-green/40 animate-ping" />
          <Check className="relative w-12 h-12 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" strokeWidth={3} />
        </div>
        <div className="mt-6 text-[10px] uppercase tracking-[0.4em] font-black text-eatup-yellow">
          Trip complete
        </div>
        <h2 className="mt-1 font-display font-black text-3xl text-white drop-shadow-md">
          +EC${earn.toFixed(2)}
        </h2>
        <p className="mt-3 text-sm text-neutral-200">
          Great work — you're back online.<br />
          <span className="text-eatup-green font-semibold">Waiting for the next order…</span>
        </p>
        <button
          onClick={onClose}
          data-testid="trip-complete-close"
          className="mt-6 w-full py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-semibold text-sm transition"
        >
          Back to home
        </button>
      </div>
    </div>
  );
}

function AcceptedTripCard({ order, isActive, onOpen }) {
  const stageMap = {
    placed:     { label: "Kitchen queued",       dot: "bg-orange-400",     chip: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
    preparing:  { label: "Cooking",              dot: "bg-amber-400",      chip: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
    ready:      { label: "Ready — head there",   dot: "bg-eatup-yellow",   chip: "bg-eatup-yellow/15 text-eatup-yellow border-eatup-yellow/50" },
    picked_up:  { label: "Picked up",            dot: "bg-sky-400",        chip: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
    on_the_way: { label: "On the way",           dot: "bg-cyan-400",       chip: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  };
  const stage = stageMap[order.status] || { label: order.status, dot: "bg-neutral-400", chip: "bg-neutral-500/15 text-neutral-300 border-neutral-500/40" };
  const orderNo = order.order_id.slice(-6).toUpperCase();
  const shortAddr = (order.address || "").split(",")[0] || order.address;
  const pickupPin = order.pickup_code ? String(order.pickup_code).padStart(4, "0") : null;
  const earn = driverEarnOf(order).toFixed(2);
  const delayedBy = Number(order.delayed_by_minutes || 0);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`accepted-trip-${order.order_id}`}
      className={`w-full text-left bg-eatup-dark-bg border rounded-2xl p-3 flex items-center gap-3 transition active:scale-[0.99] ${
        isActive
          ? "border-eatup-green/60 ring-1 ring-eatup-green/30 shadow-[0_0_28px_rgba(24,180,91,0.15)]"
          : "border-white/10 hover:border-white/30"
      }`}
    >
      <img src={order.restaurant_image} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${stage.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${stage.dot} animate-pulse`} />
            {stage.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-eatup-green font-black tabular">
            #{orderNo}
          </span>
          {isActive && (
            <span className="text-[10px] uppercase tracking-widest text-eatup-green font-black bg-eatup-green/15 border border-eatup-green/40 px-2 py-0.5 rounded-full">
              Now
            </span>
          )}
          {pickupPin && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black tabular tracking-widest bg-eatup-yellow/15 border border-eatup-yellow/40 text-eatup-yellow px-2 py-0.5 rounded-full">
              <Shield className="w-2.5 h-2.5" /> {pickupPin}
            </span>
          )}
          {delayedBy > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest bg-red-500/15 border border-red-500/40 text-red-300 px-2 py-0.5 rounded-full">
              <Clock className="w-2.5 h-2.5" /> +{delayedBy}m
            </span>
          )}
        </div>
        <div className="mt-1 text-sm font-semibold text-white truncate">{order.restaurant_name}</div>
        <div className="text-[11px] text-neutral-400 truncate flex items-center gap-1">
          <MapPin className="w-3 h-3 shrink-0" /> {shortAddr}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Earn</div>
        <div className="font-display font-black text-eatup-green tabular text-lg leading-none">EC${earn}</div>
        <div className="text-[10px] text-neutral-500 mt-0.5">{order.eta_minutes || 15}m</div>
      </div>
      <ChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
    </button>
  );
}


function ActiveTripHero({ trip, onOpenDetails, onChat }) {
  // Color-coded status for at-a-glance urgency reading from feet away.
  const stageStyle = {
    placed:     { strip: "from-orange-500 to-orange-600", chip: "bg-orange-500/20 text-orange-300 border-orange-400/40", label: "New Request", pulse: true,  ring: "ring-orange-500/40" },
    preparing:  { strip: "from-orange-500 to-amber-500",  chip: "bg-orange-500/20 text-orange-300 border-orange-400/40", label: "Kitchen Cooking", pulse: true,  ring: "ring-orange-500/40" },
    ready:      { strip: "from-amber-400 to-eatup-yellow", chip: "bg-eatup-yellow/25 text-eatup-yellow border-eatup-yellow/50", label: "Ready to Grab", pulse: true,  ring: "ring-eatup-yellow/50" },
    picked_up:  { strip: "from-sky-500 to-cyan-500",       chip: "bg-sky-500/20 text-sky-300 border-sky-400/40",  label: "Picked Up · Heading to Customer", pulse: false, ring: "ring-sky-500/40" },
    on_the_way: { strip: "from-cyan-500 to-teal-500",      chip: "bg-cyan-500/20 text-cyan-300 border-cyan-400/40", label: "On the Way", pulse: false, ring: "ring-cyan-500/40" },
    delivered:  { strip: "from-eatup-green to-emerald-500", chip: "bg-eatup-green/20 text-eatup-green border-eatup-green/40", label: "Delivered", pulse: false, ring: "ring-eatup-green/40" },
  }[trip.status] || { strip: "from-neutral-500 to-neutral-600", chip: "bg-neutral-500/20 text-neutral-300 border-neutral-400/40", label: trip.status, pulse: false, ring: "ring-neutral-500/40" };

  const pickupPin = trip.pickup_code ? String(trip.pickup_code).padStart(4, "0") : null;
  const earn = driverEarnOf(trip).toFixed(2);
  const showChat = trip.status === "on_the_way";
  const shortAddr = (trip.address || "").split(",")[0] || trip.address;

  return (
    <div className="absolute top-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[720px] space-y-3 z-10">
      {/* PIN card during the awaiting-handoff window — driver holds this up. */}
      {["placed", "preparing", "ready"].includes(trip.status) && pickupPin && (
        <div
          data-testid="driver-pickup-code-card"
          className="bg-eatup-yellow text-eatup-charcoal rounded-3xl shadow-2xl p-4 flex items-center gap-4 ring-2 ring-eatup-yellow/40 animate-[fadeUp_0.35s_ease-out]"
        >
          <div className="w-11 h-11 rounded-2xl bg-eatup-charcoal text-eatup-yellow flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] font-black">Show this at the restaurant</div>
            <div className="text-xs font-semibold text-eatup-charcoal/80">Staff will enter this PIN to release the order</div>
          </div>
          <div
            data-testid="driver-pickup-code"
            className="font-display font-black text-3xl sm:text-4xl tabular tracking-[0.35em] pr-1 select-all"
          >
            {pickupPin}
          </div>
        </div>
      )}

      {/* Hero action card — full area is the button */}
      <div
        role="button"
        tabIndex={0}
        onClick={onOpenDetails}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetails(); } }}
        data-testid="active-trip-card"
        className={`relative w-full text-left bg-white text-eatup-charcoal rounded-[28px] shadow-2xl overflow-hidden cursor-pointer transition active:scale-[0.995] focus:outline-none focus:ring-4 ${stageStyle.ring}`}
      >
        {/* Color strip */}
        <div className={`h-2.5 bg-gradient-to-r ${stageStyle.strip}`} />

        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <img src={trip.restaurant_image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shrink-0 shadow-md" />
            <div className="flex-1 min-w-0">
              <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest font-black ${stageStyle.chip}`}>
                {stageStyle.pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
                {stageStyle.label}
              </div>
              <h2 className="mt-2 font-display font-black text-2xl sm:text-3xl leading-tight truncate" data-testid="active-trip-restaurant">
                {trip.restaurant_name}
              </h2>
              <div className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500 truncate" data-testid="active-trip-area">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{shortAddr}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">You earn</div>
              <div className="font-display font-black text-3xl sm:text-4xl tabular text-eatup-green leading-none mt-0.5" data-testid="active-trip-earn">
                EC${earn}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 tabular" data-testid="active-trip-earn-breakdown">
                {trip.driver_base_pay != null ? (
                  <>Base EC${Number(trip.driver_base_pay).toFixed(2)} + {Number(trip.distance_km || 0).toFixed(1)} km × EC${Number(trip.driver_per_km_pay || 0).toFixed(2)} + tip EC${Number(trip.tip || 0).toFixed(2)}</>
                ) : (
                  <>Delivery EC${(trip.delivery_fee || 0).toFixed(2)} + tip EC${(trip.tip || 0).toFixed(2)}</>
                )}
              </div>
            </div>
          </div>

          {/* Meta strip */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">
              <Package className="w-3.5 h-3.5" /> {trip.items.length} item{trip.items.length !== 1 ? "s" : ""}
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-semibold text-neutral-700">
              <Clock className="w-3.5 h-3.5" /> ETA {trip.eta_minutes || 15} min
            </div>
            {/* Turn-by-turn navigation — opens Google Maps directions in a new tab. Uses
                the stored coords when available (preferred), else the raw address. */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const goingToRestaurant = ["placed", "preparing", "ready", "picked_up"].includes(trip.status);
                const dest = goingToRestaurant
                  ? (trip.pickup_lat != null ? `${trip.pickup_lat},${trip.pickup_lng}` : encodeURIComponent(trip.restaurant_address || trip.restaurant_name))
                  : (trip.dropoff_lat != null ? `${trip.dropoff_lat},${trip.dropoff_lng}` : encodeURIComponent(trip.address));
                const url = `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${dest}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              data-testid="driver-navigate-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-sky-500 text-white px-4 py-1.5 text-xs font-black uppercase tracking-widest shadow-md hover:bg-sky-600 transition"
              aria-label="Open turn-by-turn navigation"
              title="Open in Google Maps"
            >
              <Navigation className="w-3.5 h-3.5" /> Navigate
            </button>
            {showChat && (
              <button
                onClick={(e) => { e.stopPropagation(); onChat(); }}
                data-testid="driver-chat-customer-btn"
                className="relative inline-flex items-center gap-1.5 rounded-full bg-eatup-green text-white px-4 py-1.5 text-xs font-black uppercase tracking-widest shadow-md hover:bg-eatup-green-dark transition"
                aria-label="Chat with customer"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Chat
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-eatup-yellow border-2 border-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StickyTripAction({ trip, onTakePickup, onTakeDelivery, onOpenDetails }) {
  // The sticky bar decides its own CTA based on the stage — so the driver's
  // thumb always lands on the right next-action.
  let cta = null;
  if (trip.status === "ready") {
    cta = {
      label: "Head to Counter · Food Ready",
      sub: `Show PIN ${trip.pickup_code ? String(trip.pickup_code).padStart(4, "0") : ""} — restaurant releases the bag`,
      icon: Utensils,
      color: "from-eatup-yellow to-amber-500",
      onClick: onOpenDetails,
      testid: "sticky-head-to-counter",
      pulse: true,
    };
  } else if (["placed", "preparing"].includes(trip.status)) {
    cta = {
      label: "Show PIN & Wait for Handoff",
      sub: `Restaurant enters PIN ${trip.pickup_code ? String(trip.pickup_code).padStart(4, "0") : ""} to release`,
      icon: Shield,
      color: "from-orange-500 to-orange-600",
      onClick: onOpenDetails,
      testid: "sticky-await-handoff",
      pulse: true,
    };
  } else if (trip.status === "picked_up") {
    cta = {
      label: "Take Pickup Photo",
      sub: "Verify the food leaving the restaurant to start the trip",
      icon: Camera,
      color: "from-sky-500 to-cyan-500",
      onClick: onTakePickup,
      testid: "sticky-take-pickup",
      pulse: true,
    };
  } else if (trip.status === "on_the_way") {
    cta = {
      label: "Complete Delivery",
      sub: "Snap the drop-off photo to finish this trip",
      icon: CheckCircle2,
      color: "from-eatup-green to-emerald-500",
      onClick: onTakeDelivery,
      testid: "sticky-complete-delivery",
      pulse: true,
    };
  }
  if (!cta) return null;
  const Icon = cta.icon;
  return (
    <div
      data-testid="sticky-trip-action-bar"
      className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-2xl mx-auto p-3 sm:p-4">
        <button
          onClick={cta.onClick}
          data-testid={cta.testid}
          className={`group w-full rounded-[28px] bg-gradient-to-r ${cta.color} text-white shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)] px-5 py-4 flex items-center gap-4 active:scale-[0.98] transition ring-4 ring-white/5`}
        >
          <div className={`w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0 ${cta.pulse ? "animate-pulse" : ""}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="font-display font-black text-lg sm:text-xl leading-tight truncate">{cta.label}</div>
            <div className="text-[11px] sm:text-xs opacity-90 font-semibold truncate">{cta.sub}</div>
          </div>
          <ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition" />
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, sub, value, yellow, accent, onClick, testid, icon: Icon }) {
  // Big tappable stat card — replaces MiniStat. Full card is the button.
  const valueCls = yellow
    ? "text-eatup-yellow"
    : accent
      ? "text-eatup-green"
      : "text-white";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group relative text-left rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-eatup-green transition p-4 active:scale-[0.98]"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] font-black text-neutral-400">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${valueCls} opacity-70 group-hover:opacity-100 transition`} />}
      </div>
      <div className={`mt-1.5 font-display font-black text-3xl sm:text-4xl tabular leading-none ${valueCls}`}>
        {value}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-neutral-500 font-semibold">
        <span>{sub || " "}</span>
        <ChevronRight className="w-3 h-3 opacity-40 group-hover:opacity-100 transition" />
      </div>
    </button>
  );
}

function MiniStat({ label, value, yellow, onClick, testid }) {
  if (onClick) {
    return (
      <button
        onClick={onClick}
        data-testid={testid}
        className="text-center rounded-2xl px-2 py-2 -mx-2 hover:bg-white/5 focus:bg-white/5 focus:outline-none transition group cursor-pointer"
      >
        <div className={`font-display font-bold text-xl tabular ${yellow ? "text-eatup-yellow" : "text-white"} group-hover:opacity-90`}>{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold flex items-center justify-center gap-1">
          {label}
          <ChevronRight className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100 transition" />
        </div>
      </button>
    );
  }
  return (
    <div className="text-center" data-testid={testid}>
      <div className={`font-display font-bold text-xl tabular ${yellow ? "text-eatup-yellow" : "text-white"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-300 font-bold">{label}</div>
    </div>
  );
}

function OfferCard({ o, onView, onQuickAccept }) {
  const earn = driverEarnOf(o);
  return (
    <button
      onClick={onView}
      data-testid={`offer-${o.order_id}`}
      className="w-full text-left bg-eatup-dark-bg border border-white/10 rounded-2xl p-4 flex items-center gap-4 hover:border-eatup-green/40 transition active:scale-[0.99]"
    >
      <div className="text-center shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-eatup-yellow font-bold">Pay</div>
        <div className="font-display font-bold text-2xl tabular">EC${earn.toFixed(2)}</div>
      </div>
      <div className="w-px h-12 bg-white/10 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-neutral-400 mb-1">
          <MapPin className="w-3 h-3 text-eatup-green shrink-0" />
          <span className="truncate">Pickup · {o.restaurant_name}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <Navigation className="w-3 h-3 text-eatup-yellow shrink-0" />
          <span className="truncate">{o.address?.split(",")[0]}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-neutral-500">
          <Package className="w-3 h-3" /> {o.items?.length || 0} items · #{o.order_id.slice(-6).toUpperCase()}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <button
          onClick={onQuickAccept}
          data-testid={`accept-${o.order_id}`}
          className="bg-eatup-green hover:bg-eatup-green-dark text-white font-semibold px-4 py-1.5 rounded-full text-sm transition"
        >
          Accept
        </button>
        <span className="text-[10px] text-neutral-500 font-semibold uppercase tracking-widest">Tap card for details</span>
      </div>
    </button>
  );
}

function OrderDetailsSheet({ order, onClose, onAccept, onStatus }) {
  const [copied, setCopied] = useState(false);
  const [showCustomerContact, setShowCustomerContact] = useState(false);
  const earn = driverEarnOf(order);
  const timeline = order.timeline || [];

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(order.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="order-details-sheet"
    >
      <div
        className="bg-white text-eatup-charcoal w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden fade-up max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative">
          <img src={order.restaurant_image} alt="" className="w-full h-32 sm:h-40 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur shadow-md flex items-center justify-center"
            data-testid="close-order-details"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <div className="text-[10px] uppercase tracking-widest font-bold text-eatup-yellow">
              Order #{order.order_id.slice(-6).toUpperCase()}
            </div>
            <div className="font-display text-2xl font-bold leading-tight">{order.restaurant_name}</div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
          {/* Pickup code — only shown while driver still needs to pick up */}
          {order.pickup_code && !["delivered", "cancelled"].includes(order.status) && !order.pickup_verified && (
            <div className="rounded-3xl bg-eatup-yellow/25 border-2 border-dashed border-eatup-yellow p-5 text-center" data-testid="driver-pickup-code-card">
              <div className="flex items-center justify-center gap-2 text-eatup-charcoal font-bold text-[10px] uppercase tracking-widest">
                <Shield className="w-3.5 h-3.5" /> Show this to the restaurant
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                {String(order.pickup_code).padStart(4, "0").split("").map((d, i) => (
                  <div key={i} className="w-14 h-16 sm:w-16 sm:h-20 rounded-2xl bg-white shadow-sm flex items-center justify-center font-display font-extrabold text-3xl sm:text-4xl tabular text-eatup-charcoal" data-testid={`pickup-digit-${i}`}>
                    {d}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] text-eatup-charcoal/70">They'll type it in to release your order</div>
            </div>
          )}

          {order.pickup_verified && !order.pickup_photo_image && (
            <div className="rounded-3xl bg-eatup-green/10 border border-eatup-green/30 p-4 flex items-center gap-3" data-testid="verified-awaiting-photo">
              <div className="w-11 h-11 rounded-2xl bg-eatup-green text-white flex items-center justify-center shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-semibold">Verified by restaurant</div>
                <div className="text-xs text-neutral-500">Snap a photo of the food before leaving — customer & admin will see it.</div>
              </div>
            </div>
          )}

          {/* Pickup photo preview */}
          {order.pickup_photo_image && (
            <div className="rounded-3xl overflow-hidden border border-eatup-yellow/40 bg-eatup-yellow/10" data-testid="driver-pickup-photo-card">
              <img src={order.pickup_photo_image} alt="Pickup photo" className="w-full aspect-video object-cover" />
              <div className="p-3 flex items-center gap-2 text-xs">
                <Camera className="w-3.5 h-3.5 text-eatup-charcoal" />
                <span className="font-semibold text-eatup-charcoal">Pickup photo shared with customer</span>
              </div>
            </div>
          )}

          {/* Proof of delivery preview */}
          {order.proof_of_delivery_image && (
            <div className="rounded-3xl overflow-hidden border border-eatup-green/40 bg-eatup-green/5" data-testid="driver-proof-image-card">
              <img src={order.proof_of_delivery_image} alt="Delivery proof" className="w-full aspect-video object-cover" />
              <div className="p-3 flex items-center gap-2 text-xs">
                <Camera className="w-3.5 h-3.5 text-eatup-green" />
                <span className="font-semibold text-eatup-green">Delivery proof sent to customer & admin</span>
              </div>
            </div>
          )}

          {/* Earnings summary */}
          <div className="rounded-3xl bg-eatup-charcoal text-white p-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-eatup-yellow font-bold">You earn</div>
              <div className="font-display text-3xl font-extrabold tabular">EC${earn.toFixed(2)}</div>
              <div className="text-xs text-white/60 mt-0.5">
                {order.driver_base_pay != null ? (
                  <>Base EC${Number(order.driver_base_pay).toFixed(2)} + {Number(order.distance_km || 0).toFixed(1)} km × EC${Number(order.driver_per_km_pay || 0).toFixed(2)} + tip EC${Number(order.tip || 0).toFixed(2)}</>
                ) : (
                  <>Delivery EC${(order.delivery_fee || 0).toFixed(2)} + tip EC${(order.tip || 0).toFixed(2)}</>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Payment</div>
              <div className="text-sm font-semibold capitalize">{order.payment_method.replace("_", " ")}</div>
              <div className={`mt-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${order.payment_status === "paid" ? "bg-eatup-green/20 text-eatup-green" : "bg-white/10 text-white/70"}`}>
                {order.payment_status}
              </div>
            </div>
          </div>

          {/* Route */}
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Route</div>
            <div className="border border-neutral-200 rounded-3xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-1 w-3 h-3 rounded-full bg-eatup-yellow shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Pickup</div>
                  <div className="font-semibold">{order.restaurant_name}</div>
                  <div className="text-sm text-neutral-500 truncate">{order.restaurant_id}</div>
                </div>
                <button className="text-xs font-semibold text-eatup-green px-3 py-1.5 rounded-full bg-eatup-green/10 flex items-center gap-1" data-testid="nav-pickup-btn" onClick={(e) => {
                  e.stopPropagation();
                  const q = encodeURIComponent(order.restaurant_name || order.restaurant_id);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
                }}>
                  <Navigation className="w-3 h-3" /> Navigate
                </button>
              </div>
              <div className="ml-1.5 border-l-2 border-dotted border-neutral-300 h-6" />
              <div className="flex items-start gap-3">
                <div className="mt-1 w-3 h-3 rounded-full bg-eatup-green shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Dropoff</div>
                  <div className="font-semibold">{order.address}</div>
                  {order.instructions && (
                    <div className="mt-1 text-xs text-neutral-500 bg-eatup-gray rounded-xl px-3 py-2">
                      <span className="font-semibold text-eatup-charcoal">Note: </span>{order.instructions}
                    </div>
                  )}
                </div>
                <button onClick={(e) => {
                  e.stopPropagation();
                  const q = encodeURIComponent(order.address || "");
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
                }} className="text-xs font-semibold text-eatup-green px-3 py-1.5 rounded-full bg-eatup-green/10 flex items-center gap-1" data-testid="nav-dropoff-btn">
                  <Navigation className="w-3 h-3" /> Navigate
                </button>
                <button onClick={copyAddress} className="text-xs font-semibold text-eatup-charcoal px-3 py-1.5 rounded-full bg-eatup-gray flex items-center gap-1" data-testid="copy-address-btn">
                  {copied ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>

          {/* Customer — fully clickable card that opens the contact modal */}
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Customer</div>
            <button
              type="button"
              onClick={() => setShowCustomerContact(true)}
              data-testid="driver-customer-card"
              className="w-full border border-neutral-200 hover:border-eatup-green/40 hover:bg-eatup-green/[0.03] rounded-3xl p-4 flex items-center gap-3 text-left transition active:scale-[0.995]"
            >
              <div className="w-11 h-11 rounded-full bg-eatup-charcoal text-white flex items-center justify-center font-bold">
                {(order.customer_name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{order.customer_name}</div>
                <div className="text-xs text-neutral-500 truncate">{order.customer_phone}</div>
              </div>
              <div className="w-8 h-8 rounded-full bg-eatup-green/10 text-eatup-green flex items-center justify-center shrink-0">
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          </div>

          {/* Items */}
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2 flex items-center justify-between">
              <span>Order items</span>
              <span className="text-neutral-400 normal-case tracking-normal">{order.items.length} items</span>
            </div>
            <div className="border border-neutral-200 rounded-3xl divide-y divide-neutral-100">
              {order.items.map((it) => (
                <div key={it.item_id} className="p-4 flex items-center gap-3" data-testid={`detail-item-${it.item_id}`}>
                  <div className="w-9 h-9 rounded-lg bg-eatup-green/10 text-eatup-green font-bold flex items-center justify-center tabular">
                    {it.quantity}×
                  </div>
                  {it.image_url && <img src={it.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{it.name}</div>
                    {it.notes && <div className="text-xs text-neutral-500 truncate">Note: {it.notes}</div>}
                  </div>
                  <div className="text-sm font-semibold tabular">${(it.price * it.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Bill</div>
            <div className="border border-neutral-200 rounded-3xl p-4 space-y-1.5 text-sm">
              <Row label="Subtotal" value={`EC$${order.subtotal.toFixed(2)}`} />
              <Row
                label={order.distance_km ? `Delivery (${Number(order.distance_km).toFixed(1)} km)` : "Delivery"}
                value={`EC$${order.delivery_fee.toFixed(2)}`}
              />
              <Row label="Platform fee" value={`EC$${(order.platform_flat_fee ?? order.service_charge).toFixed(2)}`} />
              {(order.small_order_fee || 0) > 0 && (
                <Row label="Small-order fee" value={`EC$${order.small_order_fee.toFixed(2)}`} />
              )}
              <Row label="Tax" value={`EC$${order.tax.toFixed(2)}`} />
              <Row label="Tip" value={`EC$${order.tip.toFixed(2)}`} />
              {order.discount > 0 && <Row label={`Promo ${order.promo_code}`} value={`- EC$${order.discount.toFixed(2)}`} accent />}
              <div className="h-px bg-neutral-100 my-1" />
              <Row label="Customer total" value={`EC$${order.total.toFixed(2)}`} bold />
            </div>
          </div>

          {/* Timeline */}
          {timeline.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Timeline</div>
              <div className="border border-neutral-200 rounded-3xl p-4 space-y-2">
                {timeline.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-eatup-green" />
                    <div className="flex-1 font-medium capitalize">{t.status.replace("_", " ")}</div>
                    <div className="text-xs text-neutral-500">{new Date(t.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer actions */}
        <div className="border-t border-neutral-100 p-4 shrink-0 space-y-2">
          {onAccept && (
            <button
              onClick={onAccept}
              data-testid="details-accept-btn"
              className="w-full bg-eatup-green hover:bg-eatup-green-dark text-white rounded-full py-3.5 font-display font-bold text-base transition flex items-center justify-between px-5"
            >
              <span>Accept this delivery</span>
              <span className="tabular">EC${earn.toFixed(2)}</span>
            </button>
          )}
          {onStatus && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { s: "picked_up", label: "Picked up", icon: MapPin },
                { s: "on_the_way", label: "On the way", icon: Navigation },
                { s: "delivered", label: "Delivered", icon: Bike },
              ].map((btn) => {
                const Icon = btn.icon;
                const active = order.status === btn.s;
                return (
                  <button
                    key={btn.s}
                    onClick={() => onStatus(btn.s)}
                    data-testid={`details-status-${btn.s}`}
                    className={`flex flex-col items-center gap-1 py-3 rounded-2xl border font-semibold text-xs transition ${
                      active ? "bg-eatup-green border-eatup-green text-white" : "border-neutral-200 text-neutral-700 hover:bg-neutral-100"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {btn.label}
                  </button>
                );
              })}
            </div>
          )}
          {!onAccept && !onStatus && (
            <button
              onClick={onClose}
              data-testid="details-close-btn"
              className="w-full bg-eatup-gray hover:bg-neutral-200 text-eatup-charcoal rounded-full py-3.5 font-semibold transition"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {showCustomerContact && (
        <ContactDetailsModal
          role="customer"
          direction="driver_to_customer"
          order={order}
          contact={{
            name: order.customer_name,
            phone: order.customer_phone,
            address: order.address,
          }}
          onClose={() => setShowCustomerContact(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, accent, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-bold text-eatup-charcoal" : "text-neutral-500"}`}>{label}</span>
      <span className={`tabular ${accent ? "text-eatup-green font-semibold" : bold ? "font-bold" : "font-semibold"}`}>{value}</span>
    </div>
  );
}

// Driver earnings estimator — mirrors backend spec (EC$ per marketplace rules):
// base per trip + per-km × distance + 100% tip. Uses the values the backend
// stamped onto the order so this preview matches what the driver actually
// collects. Defaults kept for the rare offer arriving without stamps.
const DEFAULT_DRIVER_BASE_PAY = 8.0;
const DEFAULT_DRIVER_PER_KM_PAY = 1.5;

// Total driver earn on a completed/active order using the new model, with a
// legacy fallback for orders placed before the marketplace refactor.
function driverEarnOf(o) {
  if (o?.driver_base_pay != null) {
    return (Number(o.driver_base_pay) || 0)
      + (Number(o.driver_per_km_pay) || 0) * (Number(o.distance_km) || 0)
      + (Number(o.tip) || 0)
      + (Number(o.tip_bonus) || 0);
  }
  return (Number(o?.delivery_fee) || 0) + (Number(o?.tip) || 0) + (Number(o?.tip_bonus) || 0);
}

function haversineKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function estimateEarnings(order) {
  const pickup = order.pickup_lat != null ? { lat: order.pickup_lat, lng: order.pickup_lng } : null;
  const dropoff = order.dropoff_lat != null ? { lat: order.dropoff_lat, lng: order.dropoff_lng } : null;
  const measured = haversineKm(pickup, dropoff);
  const distanceKm = order.distance_km != null ? Number(order.distance_km) : measured;
  const base = order.driver_base_pay != null ? Number(order.driver_base_pay) : DEFAULT_DRIVER_BASE_PAY;
  const perKm = order.driver_per_km_pay != null ? Number(order.driver_per_km_pay) : DEFAULT_DRIVER_PER_KM_PAY;
  const distanceCharge = distanceKm != null ? distanceKm * perKm : 0;
  const tip = Number(order.tip) || 0;
  const total = Math.round((base + distanceCharge + tip) * 100) / 100;
  return {
    total,
    base,
    distanceKm: distanceKm != null ? Math.round(distanceKm * 10) / 10 : null,
    distanceCharge: Math.round(distanceCharge * 100) / 100,
    tip,
  };
}

function OfferModal({ offer, onAccept, onDecline, onTimeout }) {
  const OFFER_SECONDS = 120;
  const [countdown, setCountdown] = useState(OFFER_SECONDS);
  const alertRef = useRef(null);

  const stopAlert = () => {
    if (alertRef.current) {
      try { alertRef.current.stop(); } catch { /* noop */ }
      alertRef.current = null;
    }
  };

  useEffect(() => {
    // Looping Caribbean alert — 30% → 100% over 30s, stops on Accept/Timeout.
    alertRef.current = startCaribbeanAlert();
    return () => stopAlert();
  }, []);

  useEffect(() => {
    if (countdown <= 0) { stopAlert(); onTimeout(); return; }
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onTimeout]);

  const wrap = (fn) => () => { stopAlert(); fn(); };

  const est = estimateEarnings(offer);
  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  const timeLabel = `${mins}:${String(secs).padStart(2, "0")}`;
  const urgent = countdown <= 20;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="offer-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Heavily blurred, darkened backdrop so nothing behind competes for attention */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-2xl backdrop-saturate-150" />

      {/* Foreground card — bold, floating, glassmorphic */}
      <div
        className={`relative w-full max-w-md sm:max-w-lg rounded-[32px] border-2 pulse-sunset-border p-6 sm:p-8 shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10 flex flex-col max-h-[92vh] overflow-hidden ${
          urgent ? "border-red-400/70" : "border-eatup-yellow/60"
        }`}
        style={{
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(242,201,76,0.18) 0%, rgba(24,180,91,0.10) 45%, rgba(20,20,22,0.85) 100%)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
        }}
      >
        {/* Big attention-grabbing label at top */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-eatup-yellow opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-eatup-yellow" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-eatup-yellow">
              New delivery
            </span>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-bold tabular ${urgent ? "text-red-400 animate-pulse" : "text-white"}`}>
            <Clock className="w-3.5 h-3.5" /> {timeLabel}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-5 flex flex-col items-center">
          {/* Earnings ring */}
          <div className="relative w-40 h-40 sm:w-44 sm:h-44 rounded-full pulse-sunset">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="46" fill="none"
                stroke={urgent ? "#EF4444" : "#18B45B"}
                strokeWidth="6" strokeLinecap="round"
                strokeDasharray={289}
                strokeDashoffset={289 * (1 - countdown / OFFER_SECONDS)}
                className="transition-all duration-1000 linear"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[10px] uppercase tracking-widest text-eatup-yellow font-bold">Estimated earnings</div>
              <div className="font-display text-4xl sm:text-5xl font-black tabular text-white leading-none mt-1 drop-shadow-lg">
                ${est.total.toFixed(2)}
              </div>
              {est.distanceKm != null && (
                <div className="text-[11px] text-neutral-300 mt-1">{est.distanceKm} km · ~{Math.max(5, Math.round(est.distanceKm * 3))} min</div>
              )}
            </div>
          </div>

          <h2 className="mt-5 font-display text-2xl sm:text-3xl font-black text-white text-center drop-shadow-md">
            First to accept — keeps it
          </h2>

          {/* Earnings breakdown */}
          <div className="mt-4 w-full rounded-2xl bg-eatup-green/15 border border-eatup-green/30 p-3 text-xs text-neutral-200 grid grid-cols-3 gap-1 text-center" data-testid="offer-earnings-breakdown">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">Base</div>
              <div className="font-display font-bold tabular text-white mt-0.5">EC${est.base.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">Distance</div>
              <div className="font-display font-bold tabular text-white mt-0.5">
                {est.distanceKm != null ? `+ EC$${est.distanceCharge.toFixed(2)}` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">Tip</div>
              <div className="font-display font-bold tabular text-eatup-green mt-0.5">+ EC${est.tip.toFixed(2)}</div>
            </div>
          </div>

          {/* Pickup / Dropoff card */}
          <div className="mt-4 w-full rounded-3xl bg-black/40 border border-white/15 p-4 sm:p-5 text-left backdrop-blur-md">
            <div className="flex items-start gap-3">
              <div className="w-3 h-3 rounded-full bg-eatup-yellow mt-1.5 shrink-0 shadow-[0_0_10px_rgba(242,201,76,0.7)]" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-eatup-yellow font-bold">Pickup</div>
                <div className="font-bold text-white truncate">{offer.restaurant_name}</div>
              </div>
              <div className="text-xs font-semibold text-neutral-200 bg-white/10 px-2 py-1 rounded-full">
                {offer.items?.length || 0} items
              </div>
            </div>
            <div className="ml-1.5 my-2 border-l-2 border-dotted border-white/25 h-8" />
            <div className="flex items-start gap-3">
              <div className="w-3 h-3 rounded-full bg-eatup-green mt-1.5 shrink-0 shadow-[0_0_10px_rgba(24,180,91,0.7)]" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-eatup-green font-bold">Dropoff</div>
                <div className="font-bold text-white truncate">{offer.address}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions — Accept, or Decline to pass to another driver immediately.
            The 120s timer also auto-passes the offer if the driver doesn't decide. */}
        <div className="mt-5 space-y-2 shrink-0">
          <button
            onClick={wrap(onAccept)}
            data-testid="offer-modal-accept"
            className="w-full bg-eatup-green hover:bg-eatup-green-dark rounded-full py-4 font-display font-black text-lg text-white transition shadow-[0_10px_30px_rgba(24,180,91,0.4)] active:scale-[0.98]"
          >
            Accept — EC${est.total.toFixed(2)}
          </button>
          <button
            onClick={wrap(onDecline)}
            data-testid="offer-modal-decline"
            className="w-full py-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-neutral-200 hover:text-white font-semibold text-sm transition"
          >
            Decline — pass to next driver
          </button>
          <div className="text-center text-[11px] text-neutral-400" data-testid="offer-modal-hint">
            You have <span className={`font-bold ${urgent ? "text-red-400" : "text-white"}`}>{timeLabel}</span> to decide — the offer auto-passes when time runs out.
          </div>
        </div>
      </div>
    </div>
  );
}

// Module-level flag mirror for the sound-enabled ref inside OfferModal's setInterval.
let __soundOn = false;
function soundStillOn() { return __soundOn; }
export function __setSoundOn(v) { __soundOn = v; }
